import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Stack,
  StackProps,
} from 'component-library';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { isNullish } from '@/utils/booleans';
import { SortOrder } from '@/utils/constants';

type CategoryItem = { name: string; count?: number };

type ItemListByCategory = {
  category: string;
  items: CategoryItem[];
};

type CategoriesSummaryProps = {
  data: PlotReportFactElement[];
  categoryLabel: string;
  fallbackNameLabel?: string;
  nameLabel: string;
  countLabel?: string;
  displayLimit?: number;
  italicNames?: boolean;
  keepZeroCount?: boolean;
  hideCategoryLabel?: boolean;
  hideCounts?: boolean;
  sortCategories?: SortOrder;
} & StackProps;

const groupByCategory = (
  facts: PlotReportFactElement[],
  categoryLabel: string,
  nameLabel: string,
  keepZeroCount: boolean,
  fallbackNameLabel?: string,
  countLabel?: string,
): ItemListByCategory[] =>
  facts.reduce((acc, fact) => {
    const name = (fact.labels?.find((l) => l.name === nameLabel)?.value ??
      (fallbackNameLabel ? fact.labels?.find((l) => l.name === fallbackNameLabel)?.value : undefined)) as
      | string
      | undefined;
    const rawCount = countLabel ? fact.labels?.find((l) => l.name === countLabel)?.value : fact.value;
    const count = typeof rawCount === 'string' ? parseFloat(rawCount) : rawCount;
    const category = fact.labels?.find((l) => l.name === categoryLabel)?.value as string | undefined;

    if (isNullish(category) || isNullish(name) || typeof count !== 'number' || (!keepZeroCount && count === 0)) {
      return acc;
    }

    const existsCategory = acc.find((g) => g.category === category);

    if (existsCategory) {
      const existingItem = existsCategory.items.find((i) => i.name === name);
      if (existingItem) {
        existingItem.count = (existingItem.count ?? 0) + count;
      } else {
        existsCategory.items.push({ name, count });
      }
    } else {
      acc.push({ category, items: [{ name, count }] });
    }

    return acc;
  }, [] as ItemListByCategory[]);

export const CategoriesSummary = ({
  data,
  categoryLabel,
  nameLabel,
  italicNames,
  fallbackNameLabel,
  countLabel,
  sortCategories,
  keepZeroCount = true,
  displayLimit = 3,
  hideCategoryLabel = false,
  hideCounts = false,
  ...delegated
}: CategoriesSummaryProps) => {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<ItemListByCategory | null>(null);

  const groups = groupByCategory(data, categoryLabel, nameLabel, keepZeroCount, fallbackNameLabel, countLabel)
    .map((g) => ({
      ...g,
      items: [...g.items].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      if (!sortCategories) return 0;
      const comparison = a.category.localeCompare(b.category);
      return sortCategories === SortOrder.ZA ? -comparison : comparison;
    });

  if (groups.length === 0) {
    return <span className='typography-body1 text-text-secondary'>{t('global.analysis.noData')}</span>;
  }

  return (
    <Stack spacing={6} {...delegated}>
      {groups.map((group, index) => (
        <Stack key={`${group.category}-${index}`} className='typography-body1 text-text-secondary'>
          {!hideCategoryLabel && group.category && (
            <span className='typography-body1Semibold'>
              {(group.category?.at(0) ?? '').toUpperCase() + group.category.slice(1)}
            </span>
          )}
          <ul>
            {group.items.slice(0, displayLimit).map((item, idx) => (
              <li
                className={`list-disc ml-5 ${italicNames ? 'italic' : ''}`}
                key={`${group.category}-${item.name}-${idx}`}
              >
                {item.name}
                {!hideCounts && !isNullish(item.count) ? ` (${item.count})` : ''}
              </li>
            ))}
            {group.items.length > displayLimit && (
              <li className='list-disc ml-5'>
                <Button
                  variant='text'
                  className='p-0 underline hover:bg-transparent hover:text-primary-light'
                  onClick={() => setSelectedGroup(group)}
                >
                  {t('global.analysis.seeMore')}
                </Button>
              </li>
            )}
          </ul>
        </Stack>
      ))}
      {selectedGroup && (
        <ItemsDialog
          open
          onOpenChange={(open) => !open && setSelectedGroup(null)}
          data={selectedGroup}
          italicNames={italicNames}
          hideCounts={hideCounts}
        />
      )}
    </Stack>
  );
};

const ItemsDialog: FC<DialogProps & { data: ItemListByCategory; italicNames?: boolean; hideCounts?: boolean }> = ({
  data,
  italicNames,
  hideCounts,
  ...props
}) => (
  <Dialog {...props}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{(data.category?.at(0) ?? '').toUpperCase() + data.category.slice(1)}</DialogTitle>
        <DialogBody className='max-h-[60vh] overflow-y-auto'>
          <ul>
            {data.items.map((item) => (
              <li className={`list-disc ml-5 my-2 ${italicNames ? 'italic' : ''}`} key={item.name}>
                {item.name}
                {!hideCounts && !isNullish(item.count) ? ` (${item.count})` : ''}
              </li>
            ))}
          </ul>
        </DialogBody>
      </DialogHeader>
    </DialogContent>
  </Dialog>
);
